import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Settings, User, HelpCircle, LogOut, Menu, Shield, Bell, Search, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import {
  HomeGlyph,
  PeopleGlyph,
  IntegralGlyph,
  MentorGlyph,
  ScienceGlyph,
  AssessGlyph,
  AstrologyGlyph,
  PersonalityGlyph,
} from '@/components/nav/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useIsMobile } from '@/hooks/use-mobile';
import { PsyforgeMark } from '@/components/brand/PsyforgeMark';
import { motion, useReducedMotion } from 'framer-motion';

interface FloatingHeaderProps {
  showBackButton?: boolean;
  backTo?: string;
  breadcrumbs?: { label: string; path?: string }[];
}

export const FloatingHeader: React.FC<FloatingHeaderProps> = ({ 
  showBackButton = false, 
  backTo,
  breadcrumbs 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminRole();
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();
  
  const isActive = (path: string) => location.pathname === path;

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Home', icon: HomeGlyph },
    { path: '/integral', label: 'Integral', icon: IntegralGlyph },
    { path: '/mentor', label: 'AI Mentor', icon: MentorGlyph },
    { path: '/science', label: 'Science', icon: ScienceGlyph },
  ];

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-lg"
          : "bg-transparent"
      )}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="container mx-auto px-4">
        <div className={cn(
          "flex items-center justify-between transition-all duration-300",
          scrolled ? "h-14" : "h-16"
        )}>
          {/* Left Section: Back Button / Logo */}
          <div className="flex items-center gap-3">
            {showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                {!isMobile && <span>Back</span>}
              </Button>
            )}
            
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <nav className="flex items-center gap-2 text-sm">
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {crumb.path ? (
                      <Link
                        to={crumb.path}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-foreground font-medium">{crumb.label}</span>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </React.Fragment>
                ))}
              </nav>
            ) : (
              <Link
                to="/"
                className="flex items-center gap-2 group min-w-0"
              >
                <PsyforgeMark
                  pixels={isMobile ? 28 : scrolled ? 32 : 40}
                  className="shrink-0 transition-all duration-300"
                />
                {!scrolled && (
                  <span className="font-display font-bold leading-none text-lg sm:text-xl text-foreground group-hover:text-primary transition-colors truncate">
                    Psyforge
                  </span>
                )}
              </Link>
            )}
          </div>

          {/* Desktop Navigation - Only show for authenticated users */}
          {!isMobile && user && (
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map(({ path, label }) => {
                const active = isActive(path);
                return (
                  <Link
                    key={path}
                    to={path}
                    className={cn(
                      "relative px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200",
                      active
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {active && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-lg bg-primary/10"
                        transition={
                          reduced
                            ? { duration: 0 }
                            : { type: 'spring', stiffness: 380, damping: 32 }
                        }
                        aria-hidden="true"
                      />
                    )}
                    <span className="relative z-10">{label}</span>
                  </Link>
                );
              })}
            </nav>
          )}

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {!isMobile && user && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative hover:bg-accent/50"
                >
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-accent/50"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </>
            )}

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full hover:ring-2 hover:ring-primary/50 transition-all"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.user_metadata?.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-primary-glow text-white">
                        {user.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-popover backdrop-blur-xl border-border z-50"
                >
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user.user_metadata?.display_name || 'User'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer">
                        <Shield className="mr-2 h-4 w-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/demo">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-primary hover:text-primary-glow"
                  >
                    <PersonalityGlyph className="w-4 h-4" />
                    {!isMobile && "Preview Demo"}
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg hover:shadow-primary/30 transition-all"
                  >
                    Sign In
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu */}
            {isMobile && (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 bg-background/95 backdrop-blur-xl">
                  <nav className="flex flex-col gap-4 mt-8">
                    {user ? (
                      // Authenticated user nav
                      navItems.map(({ path, label, icon: Icon }) => (
                        <Link
                          key={path}
                          to={path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={cn(
                            "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all",
                            isActive(path)
                              ? 'bg-primary/10 text-primary border border-primary/20'
                              : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                          )}
                        >
                          <Icon className="w-5 h-5" />
                          {label}
                        </Link>
                      ))
                    ) : (
                      // Unauthenticated user nav
                      <>
                        <Link
                          to="/demo"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-primary/10 text-primary border border-primary/20"
                        >
                          <Play className="w-5 h-5" />
                          Preview Demo
                        </Link>
                        <Link
                          to="/tps-assessment-hub"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50"
                        >
                          <AssessGlyph className="w-5 h-5" />
                          Take Assessment
                        </Link>
                        <Link
                          to="/integral"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50"
                        >
                          <IntegralGlyph className="w-5 h-5" />
                          Integral Assessment
                        </Link>
                        <Link
                          to="/astrology"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50"
                        >
                          <AstrologyGlyph className="w-5 h-5" />
                          Astrology Preview
                        </Link>
                        <Link
                          to="/science"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50"
                        >
                          <ScienceGlyph className="w-5 h-5" />
                          Personality Science
                        </Link>
                        <div className="pt-4 border-t border-border">
                          <Link
                            to="/auth"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-primary to-primary-glow text-white"
                          >
                            Sign In / Create Account
                          </Link>
                        </div>
                      </>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
